<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import {
  monthlySummariesApi, type NewPatient, type NewCaregiver,
} from '@/api/monthly-summaries';
import { type SerializedUser } from '@/api/users';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import { getCareerColor } from '@/utils/careers';

const props = defineProps<{
  kind: 'patients' | 'caregivers';
  queryParams: string;
  selectedMonthText: string;
}>();

const model = defineModel<boolean>({ default: false });

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const isPatients = computed(() => props.kind === 'patients');

const title = computed(() => (isPatients.value ?
  `Pacientes nuevos — ${props.selectedMonthText}` :
  `Profesionales nuevos — ${props.selectedMonthText}`));

const emptyText = computed(() => (isPatients.value ?
  'No hay pacientes nuevos en este mes.' :
  'No hay profesionales nuevos en este mes.'));

const showGroups = computed(() => !!currentUser?.value?.companyGroupsEnabled);
const showCategories = computed(() =>
  isPatients.value && !!currentUser?.value?.companyPatientServiceCategoriesEnabled);

const { data: patients, isPending: patientsPending } = useQuery({
  queryKey: computed(() => ['dashboardNewPatients', props.queryParams]),
  queryFn: () => monthlySummariesApi.newPatients(props.queryParams),
  enabled: computed(() => model.value && isPatients.value),
});

const { data: caregivers, isPending: caregiversPending } = useQuery({
  queryKey: computed(() => ['dashboardNewCaregivers', props.queryParams]),
  queryFn: () => monthlySummariesApi.newCaregivers(props.queryParams),
  enabled: computed(() => model.value && !isPatients.value),
});

type Chip = { text: string; color?: string; variant: 'tonal' | 'outlined'; icon?: string };
type DisplayRow = {
  id: number; fullName: string; dateLabel: string; humanDate: string; chips: Chip[];
};

function groupChips(groupNames: string[]): Chip[] {
  if (!showGroups.value) return [];

  return groupNames.map((name) => (
    { text: name, variant: 'outlined' as const, icon: 'mdi-account-group' }
  ));
}

// Row order mirrors the requested reading: name + group + category - subcategory
// (career takes the category slot for caregivers).
function patientChips(patient: NewPatient): Chip[] {
  const chips = groupChips(patient.groupNames);
  if (showCategories.value && patient.serviceCategoryName) {
    const text = patient.serviceSubcategoryName ?
      `${patient.serviceCategoryName} • ${patient.serviceSubcategoryName}` :
      patient.serviceCategoryName;
    chips.push({ text, color: 'primary', variant: 'tonal' });
  }

  return chips;
}

function caregiverChips(caregiver: NewCaregiver): Chip[] {
  const careerChip: Chip = {
    text: caregiver.humanCareer, color: getCareerColor(caregiver.career), variant: 'tonal',
  };

  return [...groupChips(caregiver.groupNames), careerChip];
}

// A patient is new by its service start; without one, by its creation date. The
// label says which date counted.
function patientDate(patient: NewPatient): Pick<DisplayRow, 'dateLabel' | 'humanDate'> {
  if (patient.humanStartsOn) {
    return { dateLabel: 'Inicio de servicio', humanDate: patient.humanStartsOn };
  }

  return { dateLabel: 'Creado', humanDate: patient.humanCreatedAt };
}

const rows = computed<DisplayRow[] | undefined>(() => {
  if (isPatients.value) {
    return patients.value?.map((patient) => ({
      id: patient.id, fullName: patient.fullName, ...patientDate(patient),
      chips: patientChips(patient),
    }));
  }

  return caregivers.value?.map((caregiver) => ({
    id: caregiver.id, fullName: caregiver.fullName,
    dateLabel: 'Ingreso', humanDate: caregiver.humanCreatedAt, chips: caregiverChips(caregiver),
  }));
});

const loading = computed(() =>
  (isPatients.value ? patientsPending.value : caregiversPending.value));
</script>

<template>
  <responsive-dialog
    v-model="model"
    :title="title"
    max-width="560"
    scrollable
  >
    <div
      v-if="loading"
      class="d-flex justify-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <p
      v-else-if="!rows || rows.length === 0"
      class="text-center text-medium-emphasis py-8"
    >
      {{ emptyText }}
    </p>
    <v-list v-else>
      <v-list-item
        v-for="row in rows"
        :key="row.id"
        class="px-0"
      >
        <div class="d-flex align-center flex-wrap ga-2">
          <span class="font-weight-medium">{{ row.fullName }}</span>
          <v-chip
            v-for="(chip, chipIndex) in row.chips"
            :key="chipIndex"
            size="small"
            :color="chip.color"
            :variant="chip.variant"
            :prepend-icon="chip.icon"
            label
          >
            {{ chip.text }}
          </v-chip>
          <span class="text-caption text-medium-emphasis ml-auto">
            {{ row.dateLabel }} {{ row.humanDate }}
          </span>
        </div>
      </v-list-item>
    </v-list>
  </responsive-dialog>
</template>
